import React from 'react';
import { Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Auth gate for the authenticated-only routes.
 *
 * COVERS: waiting for auth to RESOLVE, for the three routes mounted under it —
 * `/favourites`, `/account`, `/account/saved-searches`: 3 of 4,177 built routes.
 * While `isLoading` it renders a spinner that announces itself honestly
 * ("Checking sign-in") — it is verifying a session, not loading content (item 55).
 *
 * DOES NOT COVER: what a SIGNED-OUT visitor sees. That is deliberately each
 * page's own job, and all three already implement it
 * (`FavouritesPage` / `AccountPage` / `SavedSearchesPage`, each rendering
 * `<SignedOutPrompt>` and calling `openAuthModal({ returnUrl })` with its own
 * address). ⚠️ A page added under this gate that handles neither will show its
 * signed-in UI to a signed-out visitor — this gate will NOT catch that.
 *
 * ⚠️ WHY THERE IS NO REDIRECT HERE — do not "restore" one (item 4, operator-ruled
 * 2026-08-14). This component used to `return <Navigate to="/" replace />` for a
 * signed-out visitor. That single line meant a shared or bookmarked link to any of
 * the three routes silently rewrote the address to '/', discarded the query string,
 * and destroyed the back entry — the visitor was never told anything. It also made
 * all three pages' signed-out handling unreachable dead code, including
 * FavouritesPage's sign-in screen and, in AccountPage, the distinction between a
 * DELIBERATE sign-out (go home) and an unauthenticated arrival (invite sign-in) —
 * a distinction only that page can make, which is precisely why the decision does
 * not live here.
 */
const ProtectedRoute: React.FC = () => {
    const { isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-surface-canvas flex items-center justify-center" role="status" aria-label="Checking sign-in" aria-busy="true">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
            </div>
        );
    }

    return <Outlet />;
};

export default ProtectedRoute;
